import { getInspectionDecisionLabel, getWorkflowStatusLabel, isWorkflowFinished } from "../constants";
import type { InspectionItemData, ProjectSummary } from "../types";
import { renderPagePath } from "../view-helpers";

function countCompleted(item: InspectionItemData): number {
  return item.subitems.filter((subitem) => isWorkflowFinished(subitem.workflowStatus)).length;
}

function buildInspectionSummary(project: ProjectSummary): {
  finishedItems: number;
  totalItems: number;
  finishedSubitems: number;
  totalSubitems: number;
  notInvolvedItems: number;
} {
  const totalItems = project.inspectionItems.length;
  const finishedItems = project.inspectionItems.filter((item) => isWorkflowFinished(item.workflowStatus)).length;
  const totalSubitems = project.inspectionItems.reduce((sum, item) => sum + item.subitems.length, 0);
  const finishedSubitems = project.inspectionItems.reduce((sum, item) => sum + countCompleted(item), 0);
  const notInvolvedItems = project.inspectionItems.filter((item) => item.workflowStatus === "not_involved").length;

  return {
    finishedItems,
    totalItems,
    finishedSubitems,
    totalSubitems,
    notInvolvedItems,
  };
}

function findNextInspectionItem(project: ProjectSummary): InspectionItemData | null {
  return (
    project.inspectionItems.find((item) => item.workflowStatus !== "completed" && item.workflowStatus !== "not_involved")
    || project.inspectionItems[0]
    || null
  );
}

function renderInspectionItemCard(item: InspectionItemData): string {
  const statusClass =
    item.workflowStatus === "completed" || item.workflowStatus === "not_involved"
      ? "is-finished"
      : "is-pending";

  return `
    <button class="panel inspection-card ${statusClass}" data-item-id="${item.itemId}">
      <div class="project-card-head">
        <strong>${item.itemName}</strong>
        <span class="status-pill">${getWorkflowStatusLabel(item.workflowStatus)}</span>
      </div>
      <p class="muted">已确认完成 ${countCompleted(item)} / ${item.subitems.length} 个子项</p>
      <p class="muted">当前评定：${getInspectionDecisionLabel(item.decision)}</p>
      <p class="muted">${item.decision === "not_involved" ? "整项已设为不涉及，进入后可恢复。": "点击进入该单项，逐项查看、编辑；完成状态会根据子项评定自动更新。"}</p>
    </button>
  `;
}

export function renderInspectionItemsPage(project: ProjectSummary): string {
  const summary = buildInspectionSummary(project);
  const nextItem = findNextInspectionItem(project);

  return `
    <section class="screen">
      <header class="topbar">
        <button class="ghost-button" data-action="back-to-home">返回项目</button>
        <h1>现场评定</h1>
      </header>
      ${renderPagePath([
        { label: project.name, href: `#/projects/home?id=${project.id}` },
        { label: "现场评定" },
      ])}
      <div class="panel stack">
        <p class="eyebrow">录入主入口</p>
        <h2>${project.name}</h2>
        <p class="muted">这里按真实项目资产中的固定 17 个单项展开，后续子项录入都从这里进入。</p>
        <div class="inline-pills">
          <span class="state-pill">已完成 ${summary.finishedItems} / ${summary.totalItems} 个单项</span>
          <span class="state-pill">子项 ${summary.finishedSubitems} / ${summary.totalSubitems}</span>
          <span class="state-pill">${summary.notInvolvedItems} 个单项不涉及</span>
        </div>
        <p class="mini-meta">进入单项后逐项填写；单项和子项的完成状态都会根据当前评定自动更新，不需要额外手动确认。</p>
        ${
          nextItem
            ? `<button class="nav-button" type="button" data-action="continue-current-item" data-item-id="${nextItem.itemId}">继续录入：${nextItem.itemName}</button>`
            : ""
        }
      </div>
      <div class="stack">
        ${project.inspectionItems.map(renderInspectionItemCard).join("")}
      </div>
    </section>
  `;
}
